// =============================================
// SERVICE: API de Etiquetas e Integrações
// =============================================

import { buildApiUrl, getDefaultHeaders } from './config';

// =============================================
// INTERFACES
// =============================================

export interface Etiqueta {
  id: number;
  empresa_id: number;
  cliente_id: number;
  importacao_id?: number;
  conferencia_id?: number;
  numero_nfe?: string;
  serie_nfe?: string;
  data_emissao?: string;
  valor_nfe?: number;
  peso_total?: number;
  volumes?: number;
  cubagem?: number;
  chave_nfe?: string;
  emitente_cnpj?: string;
  emitente_nome?: string;
  destinatario_cnpj?: string;
  destinatario_nome?: string;
  dest_logradouro?: string;
  dest_numero?: string;
  dest_bairro?: string;
  dest_municipio?: string;
  dest_uf?: string;
  dest_cep?: string;
  codigo_barras?: string;
  dados_etiqueta?: Record<string, any>;
  numero_pedido?: string;
  volume_atual?: number;
  total_volumes_pedido?: number;
  status: EtiquetaStatus;
  status_detalhes?: StatusDetalhe[];
  cliente_nome?: string;
  numero_palete?: string;
  doca?: string;
  placa_veiculo?: string;
  conferente_nome?: string;
  filial_nome?: string;
  criado_em?: string;
  atualizado_em?: string;
}

export type EtiquetaStatus =
  | 'pendente'
  | 'pre_carregamento'
  | 'carregada'
  | 'em_transito'
  | 'descarregada'
  | 'conferida'
  | 'divergente'
  | 'com_ocorrencia';

export interface StatusDetalhe {
  status: string;
  data: string;
  descricao: string;
}

export interface EtiquetaEstatisticas {
  total: number;
  pendentes: number;
  pre_carregamento: number;
  carregadas: number;
  em_transito: number;
  descarregadas: number;
  conferidas: number;
  divergentes: number;
}

export interface ImportacaoEtiqueta {
  id: number;
  empresa_id: number;
  cliente_id: number;
  integracao_config_id?: number;
  tipo_arquivo: 'xml' | 'edi';
  nome_arquivo: string;
  total_etiquetas: number;
  total_importadas: number;
  total_erros: number;
  status: 'pendente' | 'processando' | 'concluido' | 'erro';
  log_processamento?: any[];
  usuario_id?: number;
  cliente_nome?: string;
  usuario_nome?: string;
  criado_em?: string;
  atualizado_em?: string;
}

export interface ClienteIntegracaoConfig {
  id?: number;
  cliente_id: number;
  empresa_id?: number;
  tipo_integracao: 'xml_nfe' | 'edi';
  nome: string;
  descricao?: string;
  ativo: boolean;
  configuracao_xml?: ConfiguracaoXml;
  configuracao_edi?: ConfiguracaoEdi;
  importar_dados_nfe: boolean;
  criado_em?: string;
  atualizado_em?: string;
}

export interface ConfiguracaoXml {
  mapeamento_tags?: Record<string, string>;
  tag_volumes?: string;
  tag_etiquetas?: string;
  tag_numero_pedido?: string;
  tag_origem_pedido?: 'prod' | 'infCpl' | 'custom';
  regex_numero_pedido?: string;
}

export interface ConfiguracaoEdi {
  codigo_linha_nfe?: string;
  codigo_linha_etiqueta?: string;
  tamanho_codigo_linha?: number;
  campos_nfe?: CampoEdi[];
  campos_etiqueta?: CampoEdi[];
}

export interface CampoEdi {
  nome: string;
  posicao_inicio: number;
  posicao_fim: number;
  descricao?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// =============================================
// REQUEST HELPER
// =============================================

const BASE_URL = '/pedidos';

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(buildApiUrl(url), {
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...(options.headers || {})
    }
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Erro ${response.status}`);
  }

  return data;
}

// =============================================
// ETIQUETAS
// =============================================

export async function listarEtiquetas(params: {
  page?: number;
  limit?: number;
  status?: string;
  cliente_id?: number;
  busca?: string;
  data_inicio?: string;
  data_fim?: string;
  importacao_id?: number;
  conferencia_id?: number;
} = {}): Promise<{ success: boolean; dados: Etiqueta[]; paginacao: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.status) searchParams.set('status', params.status);
  if (params.cliente_id) searchParams.set('cliente_id', params.cliente_id.toString());
  if (params.busca) searchParams.set('busca', params.busca);
  if (params.data_inicio) searchParams.set('data_inicio', params.data_inicio);
  if (params.data_fim) searchParams.set('data_fim', params.data_fim);
  if (params.importacao_id) searchParams.set('importacao_id', params.importacao_id.toString());
  if (params.conferencia_id) searchParams.set('conferencia_id', params.conferencia_id.toString());

  const query = searchParams.toString();
  return request(`${BASE_URL}${query ? `?${query}` : ''}`);
}

export async function buscarEtiqueta(id: number): Promise<{ success: boolean; dados: Etiqueta }> {
  return request(`${BASE_URL}/${id}`);
}

export async function atualizarStatusEtiqueta(id: number, status: EtiquetaStatus, descricao?: string): Promise<{ success: boolean; dados: Etiqueta }> {
  return request(`${BASE_URL}/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status, descricao })
  });
}

export async function criarPedido(data: Partial<Etiqueta>): Promise<{ success: boolean; message: string; dados: Etiqueta }> {
  return request(`${BASE_URL}`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function editarPedido(id: number, data: Partial<Etiqueta>): Promise<{ success: boolean; message: string; dados: Etiqueta }> {
  return request(`${BASE_URL}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deletarPedido(id: number): Promise<{ success: boolean; message: string }> {
  return request(`${BASE_URL}/${id}`, {
    method: 'DELETE'
  });
}

export async function deletarPedidoCompleto(ids: number[]): Promise<{ success: boolean; message: string }> {
  return request(`${BASE_URL}/excluir-lote`, {
    method: 'POST',
    body: JSON.stringify({ ids })
  });
}

export async function listarEtiquetasDoPedido(id: number): Promise<{ success: boolean; dados: Etiqueta[] }> {
  return request(`${BASE_URL}/${id}/etiquetas`);
}

export async function getEstatisticasEtiquetas(params: {
  cliente_id?: number;
  data_inicio?: string;
  data_fim?: string;
} = {}): Promise<{ success: boolean; dados: EtiquetaEstatisticas }> {
  const searchParams = new URLSearchParams();
  if (params.cliente_id) searchParams.set('cliente_id', params.cliente_id.toString());
  if (params.data_inicio) searchParams.set('data_inicio', params.data_inicio);
  if (params.data_fim) searchParams.set('data_fim', params.data_fim);

  const query = searchParams.toString();
  return request(`${BASE_URL}/estatisticas${query ? `?${query}` : ''}`);
}

// =============================================
// PEDIDOS AGRUPADOS
// =============================================

export interface PedidoItem {
  id: number;
  codigo_produto?: string;
  descricao_produto?: string;
  quantidade?: string;
  valor_unitario?: string;
  valor_total_produto?: string;
  ncm?: string;
  codigo_barras?: string;
  volume_atual?: number;
  status?: string;
  numero_palete?: string;
  doca?: string;
  placa_veiculo?: string;
  conferente_nome?: string;
  filial_nome?: string;
}

export interface PedidoAgrupado {
  pedido_key: string;
  numero_pedido?: string;
  numero_nfe?: string;
  serie_nfe?: string;
  emitente_cnpj?: string;
  emitente_nome?: string;
  valor_nfe?: number;
  peso_total?: number;
  volumes?: number;
  data_emissao?: string;
  dest_logradouro?: string;
  dest_numero?: string;
  dest_bairro?: string;
  dest_municipio?: string;
  dest_uf?: string;
  dest_cep?: string;
  status?: string;
  cliente_nome?: string;
  cliente_id?: number;
  total_itens: number;
  tem_ocorrencia?: boolean;
  criado_em?: string;
  itens: PedidoItem[];
}

export async function listarPedidosAgrupados(params: {
  page?: number;
  limit?: number;
  status?: string;
  cliente_id?: number;
  busca?: string;
  data_inicio?: string;
  data_fim?: string;
} = {}): Promise<{ success: boolean; dados: PedidoAgrupado[]; paginacao: Pagination }> {
  const searchParams = new URLSearchParams();
  searchParams.set('agrupar', 'pedido');
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.status) searchParams.set('status', params.status);
  if (params.cliente_id) searchParams.set('cliente_id', params.cliente_id.toString());
  if (params.busca) searchParams.set('busca', params.busca);
  if (params.data_inicio) searchParams.set('data_inicio', params.data_inicio);
  if (params.data_fim) searchParams.set('data_fim', params.data_fim);

  const query = searchParams.toString();
  return request(`${BASE_URL}?${query}`);
}

// =============================================
// IMPORTAÇÃO
// =============================================

export async function importarEtiquetas(clienteId: number, arquivo: File): Promise<{
  success: boolean;
  message: string;
  dados: {
    importacao_id: number;
    total: number;
    importadas: number;
    erros: number;
  };
}> {
  const formData = new FormData();
  formData.append('arquivo', arquivo);
  formData.append('cliente_id', clienteId.toString());

  const response = await fetch(buildApiUrl(`${BASE_URL}/importar`), {
    method: 'POST',
    headers: {
      'Authorization': getDefaultHeaders()['Authorization'] || ''
    },
    body: formData
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || `Erro ${response.status}`);
  }

  return data;
}

export async function listarImportacoes(params: {
  page?: number;
  limit?: number;
  cliente_id?: number;
  status?: string;
  tipo_arquivo?: string;
} = {}): Promise<{ success: boolean; dados: ImportacaoEtiqueta[]; paginacao: Pagination }> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.cliente_id) searchParams.set('cliente_id', params.cliente_id.toString());
  if (params.status) searchParams.set('status', params.status);
  if (params.tipo_arquivo) searchParams.set('tipo_arquivo', params.tipo_arquivo);

  const query = searchParams.toString();
  return request(`${BASE_URL}/importacoes${query ? `?${query}` : ''}`);
}

export async function buscarImportacao(id: number): Promise<{ success: boolean; dados: ImportacaoEtiqueta }> {
  return request(`${BASE_URL}/importacoes/${id}`);
}

// =============================================
// CONFIGURAÇÃO DE INTEGRAÇÃO DO CLIENTE
// =============================================

export async function listarIntegracoesCliente(clienteId: number): Promise<{ success: boolean; dados: ClienteIntegracaoConfig[] }> {
  return request(`${BASE_URL}/clientes/${clienteId}/integracoes`);
}

export async function salvarIntegracaoCliente(clienteId: number, data: Partial<ClienteIntegracaoConfig>): Promise<{ success: boolean; dados: ClienteIntegracaoConfig }> {
  return request(`${BASE_URL}/clientes/${clienteId}/integracoes`, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function deletarIntegracaoCliente(id: number): Promise<{ success: boolean }> {
  return request(`${BASE_URL}/integracoes/${id}`, {
    method: 'DELETE'
  });
}

// =============================================
// HELPERS
// =============================================

export const STATUS_LABELS: Record<EtiquetaStatus, string> = {
  pendente: 'Pendente',
  pre_carregamento: 'Pré-Carregamento',
  carregada: 'Carregada',
  em_transito: 'Em Trânsito',
  descarregada: 'Descarregada',
  conferida: 'Conferida',
  divergente: 'Divergente',
  com_ocorrencia: 'Com Ocorrência'
};

export const STATUS_COLORS: Record<EtiquetaStatus, string> = {
  pendente: '#6b7280',
  pre_carregamento: '#3b82f6',
  carregada: '#f59e0b',
  em_transito: '#eab308',
  descarregada: '#22c55e',
  conferida: '#16a34a',
  divergente: '#ef4444',
  com_ocorrencia: '#dc2626'
};

export const STATUS_ICONS: Record<EtiquetaStatus, string> = {
  pendente: 'fas fa-clock',
  pre_carregamento: 'fas fa-boxes-packing',
  carregada: 'fas fa-truck-loading',
  em_transito: 'fas fa-truck',
  descarregada: 'fas fa-dolly',
  conferida: 'fas fa-check-circle',
  divergente: 'fas fa-exclamation-triangle',
  com_ocorrencia: 'fas fa-times-circle'
};
